import { defineComponent as _defineComponent } from 'vue'
import { AxiosError } from 'axios';
import { CnhCompanyOverview, CnhFarmOverview, CnhFieldOverview, CnhGrowerOverview } from 'farmdok-rest-api';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

import i18n from '@/initI18n';

import Loading from '@/shared/components/Loading.vue';
import ButtonLink from '@/shared/components/buttons/ButtonLink.vue';
import FormFieldDatepicker from '@/shared/components/form/FormFieldDatepicker.vue';
import FormFieldDropdown from '@/shared/components/form/FormFieldDropdown.vue';
import { DropdownItem, DropdownItemsPerCategory } from '@/shared/components/form/formFieldDropdownTypes';
import { CNH_BRAND_PORTAL_URL } from '@/shared/constants';

import useCnhImport from '../composables/useCnhImport';


export default /*#__PURE__*/_defineComponent({
  __name: 'ImportLoadDataCnhFilterForm',
  setup(__props) {

const $t = i18n.translate;
const router = useRouter();
const {
  cnhCompanyTasks,
  cnhLogout,
  getCnhCompanyOverview,
  getCnhCompanyTasks,
  cnhErrorResponseHandler,
  cnhImportDataLoading,
  cnhCompanyId,
  cnhCompanyTasksFiltered,
  cnhEmail,
} = useCnhImport();

/* -- CONSTANTS -- */

const today = new Date();
const todayMinusOneMonth = new Date();
todayMinusOneMonth.setMonth(today.getMonth() - 1);

/* -- REACTIVE -- */

const companyTree = ref<CnhCompanyOverview | null>(null);
const currentGrowerObject = ref<CnhGrowerOverview>();
const currentFarmObject = ref<CnhFarmOverview | null>();
const currentFieldObject = ref<CnhFieldOverview | null>();
const growerValue = ref<DropdownItem>({ id: '0', name: '' });
const farmValue = ref<DropdownItem>({ id: '0', name: '' });
const fieldValue = ref<DropdownItem>({ id: '0', name: '' });
const loading = ref(true);

const fromDate = ref<Date>(todayMinusOneMonth);
const toDate = ref<Date>(today);

/* -- COMPUTED -- */

const growerDropdownItems = computed<DropdownItemsPerCategory[]>(() => dropdownItems(companyTree.value, 'growers'));

const farmDropdownItems = computed<DropdownItemsPerCategory[]>(() => {
  const items = dropdownItems(currentGrowerObject.value, 'farms');
  addAllItem(items[0].items, $t('Alle Farmen') ?? 'All farms');
  return items;
});

const fieldDropdownItems = computed<DropdownItemsPerCategory[]>(() => {
  const fields: CnhFieldOverview[] = [];
  let items = [];
  if (!currentFarmObject.value) {
    currentGrowerObject.value?.farms.forEach((farm: CnhFarmOverview) => {
      fields.push(...farm.fields);
    });
    items = dropdownItemsFromArray(fields);
  } else {
    items = dropdownItems(currentFarmObject.value, 'fields');
  }

  addAllItem(items[0].items, $t('Alle Felder') ?? 'All fields');
  return items;
});

/* WATCH */
watch([currentGrowerObject, currentFarmObject, currentFieldObject, fromDate, toDate], () => {
  filterTasks();
});

/* -- LIFECYCLE HOOKS -- */

onMounted(() => {
  // seems that the redirect from the login page to this page is too fast for the backend
  setTimeout(loadCnhData, 500);
});

/* -- METHODS -- */

function filterTasks() {
  const fieldIds: string[] = [];
  if (currentFieldObject.value) {
    fieldIds.push(currentFieldObject.value.id);
  } else if (currentFarmObject.value) {
    fieldIds.push(...currentFarmObject.value.fields.map((field: CnhFieldOverview) => field.id));
  } else {
    currentGrowerObject.value?.farms.forEach((farm: CnhFarmOverview) => {
      fieldIds.push(...farm.fields.map((field: CnhFieldOverview) => field.id));
    });
  }

  const matchedTasks = cnhCompanyTasks.value.filter(
    (task) =>
      fieldIds.includes(task.fieldId) &&
      new Date(task.startTime * 1000) >= fromDate.value &&
      new Date(task.endTime * 1000) <= toDate.value,
  );
  cnhCompanyTasksFiltered.value = matchedTasks;
}

/* Getters */
function dropdownItems(data: any, propertyName: string): DropdownItemsPerCategory[] {
  if (!data) return [];

  const items = [
    {
      id: '0',
      items: data[propertyName].map((item: any) => ({ id: item.id, name: item.name })),
      sort: false,
    },
  ];

  return items;
}

function dropdownItemsFromArray(array: any[]): DropdownItemsPerCategory[] {
  if (!array) return [];

  const items = [
    {
      id: 'all',
      items: array.map((item: any) => ({ id: item.id, name: item.name })),
      sort: false,
    },
  ];

  return items;
}

function addAllItem(items: any[], name: string) {
  return items.unshift({
    id: 'all',
    name,
  });
}

function openCnhBrandPortal() {
  window.open(CNH_BRAND_PORTAL_URL, '_blank', 'noopener noreferrer');
}

/* Setters */
function setGrowerValue(value: DropdownItem) {
  currentGrowerObject.value = companyTree.value?.growers.find((grower) => grower.id === value.id);
  growerValue.value = value;
  setDefaultValues();
}

function setFarmValue(value: DropdownItem) {
  currentFarmObject.value = currentGrowerObject.value?.farms.find((farm: CnhFarmOverview) => farm.id === value.id);
  farmValue.value = value;
  currentFieldObject.value = null;
  [fieldValue.value] = fieldDropdownItems.value[0].items;
}

function setFieldValue(value: DropdownItem) {
  if (value.id === 'all') {
    currentFieldObject.value = null;
    return;
  }

  const farmObject = currentGrowerObject.value?.farms.find((farm: CnhFarmOverview) =>
    farm.fields.find((field) => field.id === value.id),
  );
  if (farmObject) {
    currentFieldObject.value = farmObject.fields.find((field) => field.id === value.id);
  }
  fieldValue.value = value;
}

function setDefaultValues() {
  [farmValue.value] = farmDropdownItems.value[0].items;
  [fieldValue.value] = fieldDropdownItems.value[0].items;
  currentFarmObject.value = null;
  currentFieldObject.value = null;
}

/* API Actions */
async function loadCnhData() {
  try {
    const overviewResponse = await getCnhCompanyOverview();
    if (overviewResponse.status === 200) {
      companyTree.value = overviewResponse.data.data?.company ?? null;

      const companyId = companyTree.value?.id;
      cnhCompanyId.value = companyId;

      await getCnhCompanyTasks();
      loading.value = false;

      if (companyTree.value) {
        currentGrowerObject.value = companyTree.value?.growers[0];
        [growerValue.value] = growerDropdownItems.value[0].items;
        setDefaultValues();
      }
    }
  } catch (error) {
    cnhErrorResponseHandler(error as AxiosError);
  }
}

async function disconnectCnh() {
  try {
    const logoutResponse = await cnhLogout();
    if (logoutResponse.status === 200) {
      router.push({ name: 'activities/import/choose-method' });
    }
  } catch (error) {
    cnhErrorResponseHandler(error as AxiosError);
  }
}

return { __sfc: true,$t, router, cnhCompanyTasks, cnhLogout, getCnhCompanyOverview, getCnhCompanyTasks, cnhErrorResponseHandler, cnhImportDataLoading, cnhCompanyId, cnhCompanyTasksFiltered, cnhEmail, today, todayMinusOneMonth, companyTree, currentGrowerObject, currentFarmObject, currentFieldObject, growerValue, farmValue, fieldValue, loading, fromDate, toDate, growerDropdownItems, farmDropdownItems, fieldDropdownItems, filterTasks, dropdownItems, dropdownItemsFromArray, addAllItem, openCnhBrandPortal, setGrowerValue, setFarmValue, setFieldValue, setDefaultValues, loadCnhData, disconnectCnh, Loading, ButtonLink, FormFieldDatepicker, FormFieldDropdown }
}

})